import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useApp } from '@/presentation/hooks/use-app.js';
import { ClientLogoPicker } from '@/presentation/pages/clients/components/client-logo-picker.js';
import { CLIENT_ABBREVIATION_MAX_LENGTH, CLIENT_NAME_MAX_LENGTH } from '@/shared/constants/client.js';

interface Props {
    name: string;
    abbreviation: string;
    onNameChange: (value: string) => void;
    onAbbreviationChange: (value: string) => void;
    logoUrl: string | null;
    logoFile: File | null;
    isEdit: boolean;
    onLogoFileSelected: (file: File) => void;
    onRemoveLogoFile: () => void;
    onUploadLogoNow: () => void;
    onRemoveLogo: () => void;
    readOnly?: boolean;
}

export function ClientIdentitySection({
    name,
    abbreviation,
    onNameChange,
    onAbbreviationChange,
    logoUrl,
    logoFile,
    isEdit,
    onLogoFileSelected,
    onRemoveLogoFile,
    onUploadLogoNow,
    onRemoveLogo,
    readOnly = false
}: Props) {
    const { translate } = useApp();

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
            <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
                    {translate('clients.clientInfo')}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <ClientLogoPicker
                        logoUrl={logoUrl}
                        logoFile={logoFile}
                        isEdit={isEdit}
                        onFileSelected={onLogoFileSelected}
                        onRemoveFile={onRemoveLogoFile}
                        onUploadNow={onUploadLogoNow}
                        onRemoveLogo={onRemoveLogo}
                        readOnly={readOnly}
                    />
                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0, pt: 3.25, justifyContent: 'center' }}>
                        <TextField
                            label={`${translate('clients.nameLabel')} *`}
                            value={name}
                            onChange={(e) => onNameChange(e.target.value)}
                            size="small"
                            fullWidth
                            required
                            disabled={readOnly}
                            inputProps={{ maxLength: CLIENT_NAME_MAX_LENGTH }}
                        />
                        <TextField
                            label={`${translate('clients.abbrLabel')} *`}
                            value={abbreviation}
                            onChange={(e) => onAbbreviationChange(e.target.value.toUpperCase())}
                            size="small"
                            disabled={readOnly}
                            inputProps={{ maxLength: CLIENT_ABBREVIATION_MAX_LENGTH }}
                            sx={{ maxWidth: 200 }}
                        />
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
